// src/pages/Partner/PartnerProgrammeActivity.jsx

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Activity,
  Award,
  BookOpen,
  Building2,
  CheckCircle2,
  Clock3,
  ListChecks,
  RefreshCw,
  UserPlus,
} from "lucide-react";

import {
  Link,
  useOutletContext,
} from "react-router-dom";

import {
  DEFAULT_PAGE_SIZE,
  fetchPartnerProgrammeActivity,
} from "../../lib/partner/partnerProgrammeActivity";


const ACTIVITY_META = {
  learning: {
    label: "Learning",
    icon: BookOpen,
    classes:
      "bg-blue-50 text-blue-700 border-blue-100",
  },

  practical: {
    label: "Practical",
    icon: ListChecks,
    classes:
      "bg-green-50 text-green-700 border-green-100",
  },

  activation: {
    label: "Activation",
    icon: Award,
    classes:
      "bg-[#FFF4EC] text-[#E9782F] border-[#FFD2B5]",
  },

  enrolment: {
    label: "Enrolment",
    icon: UserPlus,
    classes:
      "bg-violet-50 text-violet-700 border-violet-100",
  },
};


function activityMeta(kind) {
  return (
    ACTIVITY_META[kind] || {
      label: "Activity",
      icon: Activity,
      classes:
        "bg-gray-50 text-gray-700 border-gray-200",
    }
  );
}


function formatDateTime(value) {
  if (!value) {
    return {
      date: "Date unavailable",
      time: "",
    };
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return {
      date: String(value),
      time: "",
    };
  }

  return {
    date:
      new Intl.DateTimeFormat(
        "en-ZA",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      ).format(date),

    time:
      new Intl.DateTimeFormat(
        "en-ZA",
        {
          hour: "2-digit",
          minute: "2-digit",
        }
      ).format(date),
  };
}


function eventTimestamp(event) {
  if (!event?.event_at) {
    return 0;
  }

  const timestamp =
    new Date(
      event.event_at
    ).getTime();

  return Number.isFinite(
    timestamp
  )
    ? timestamp
    : 0;
}


export default function PartnerProgrammeActivity() {
  const {
    access,
  } =
    useOutletContext();

  const [
    events,
    setEvents,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadingMore,
    setLoadingMore,
  ] = useState(false);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    hasMore,
    setHasMore,
  ] = useState(false);


  const loadInitial =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        try {
          if (!silent) {
            setLoading(true);
          }

          setError("");

          const rows =
            await fetchPartnerProgrammeActivity({
              limit:
                DEFAULT_PAGE_SIZE,
              offset: 0,
            });

          setEvents(rows);

          setHasMore(
            rows.length ===
              DEFAULT_PAGE_SIZE
          );
        } catch (loadError) {
          console.error(
            "Programme activity loading failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "Programme activity could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      []
    );


  useEffect(() => {
    loadInitial();
  }, [loadInitial]);


  async function handleRefresh() {
    try {
      setRefreshing(true);

      await loadInitial({
        silent: true,
      });
    } finally {
      setRefreshing(false);
    }
  }


  async function handleLoadMore() {
    if (
      loadingMore ||
      !hasMore
    ) {
      return;
    }

    try {
      setLoadingMore(true);
      setError("");

      const rows =
        await fetchPartnerProgrammeActivity({
          limit:
            DEFAULT_PAGE_SIZE,

          offset:
            events.length,
        });

      setEvents(
        (current) => {
          const existingIds =
            new Set(
              current.map(
                (event) =>
                  event.event_id
              )
            );

          const newRows =
            rows.filter(
              (event) =>
                !existingIds.has(
                  event.event_id
                )
            );

          return [
            ...current,
            ...newRows,
          ];
        }
      );

      setHasMore(
        rows.length ===
          DEFAULT_PAGE_SIZE
      );
    } catch (loadError) {
      console.error(
        "Loading more programme activity failed:",
        loadError
      );

      setError(
        loadError?.message ||
          "More programme activity could not be loaded."
      );
    } finally {
      setLoadingMore(false);
    }
  }


  /*
   * The RPC already orders these by
   * event_at DESC.
   *
   * Keeping a frontend sort here makes
   * the page resilient when additional
   * pages are appended.
   */
  const orderedEvents =
    useMemo(
      () =>
        [...events].sort(
          (a, b) =>
            eventTimestamp(b) -
            eventTimestamp(a)
        ),
      [events]
    );


  const role =
    String(
      access?.partner_role ||
        ""
    );

  const isFacilitator =
    role ===
    "facilitator";


  return (
    <>
      {/* ==================================================
          HEADER
      ================================================== */}

      <header className="rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm md:p-8">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Partner · Programme
            </p>

            <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] text-[#333333] md:text-[42px]">
              Programme activity
            </h1>

            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">
              {isFacilitator
                ? "Recent programme actions from learners assigned to you across the cohorts you can access."
                : "Recent learner programme actions across all cohorts available to your account."}
            </p>
          </div>

          <button
            type="button"
            onClick={
              handleRefresh
            }
            disabled={
              refreshing ||
              loading
            }
            className="inline-flex flex-none items-center justify-center rounded-full border border-[#E4E4E4] bg-white px-5 py-3 text-sm font-black text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF9F5] hover:text-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              className={[
                "mr-2 h-4 w-4",
                refreshing
                  ? "animate-spin"
                  : "",
              ].join(" ")}
            />

            Refresh
          </button>
        </div>
      </header>


      {/* ==================================================
          ERROR
      ================================================== */}

      {error && (
        <div
          role="alert"
          className="mt-5 rounded-[22px] border border-red-200 bg-red-50 px-5 py-4"
        >
          <p className="text-sm font-black text-red-800">
            Programme activity could
            not be loaded completely.
          </p>

          <p className="mt-1 text-sm text-red-700">
            {error}
          </p>

          <button
            type="button"
            onClick={
              handleRefresh
            }
            className="mt-3 inline-flex items-center text-sm font-black text-red-800"
          >
            <RefreshCw className="mr-2 h-4 w-4" />

            Try again
          </button>
        </div>
      )}


      {/* ==================================================
          FEED
      ================================================== */}

      <section className="mt-7">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Activity feed
            </p>

            <h2 className="mt-1 text-xl font-black text-[#333333]">
              Recent learner actions
            </h2>
          </div>

          {!loading &&
            orderedEvents.length >
              0 && (
              <p className="text-xs font-bold text-[#999999]">
                {
                  orderedEvents.length
                }{" "}
                {orderedEvents.length ===
                1
                  ? "action"
                  : "actions"}{" "}
                loaded
              </p>
            )}
        </div>


        {loading ? (
          <LoadingState />
        ) : orderedEvents.length ===
          0 ? (
          <EmptyState />
        ) : (
          <div className="overflow-hidden rounded-[26px] border border-[#EFEFEF] bg-white shadow-sm">
            <div className="divide-y divide-[#EFEFEF]">
              {orderedEvents.map(
                (event) => (
                  <ProgrammeActivityRow
                    key={
                      event.event_id
                    }
                    event={
                      event
                    }
                  />
                )
              )}
            </div>
          </div>
        )}


        {!loading &&
          hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={
                  handleLoadMore
                }
                disabled={
                  loadingMore
                }
                className="inline-flex items-center justify-center rounded-full border border-[#FF914D] bg-white px-6 py-3 text-sm font-black text-[#FF914D] transition hover:bg-[#FFF4EC] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingMore && (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                )}

                {loadingMore
                  ? "Loading…"
                  : "Load more activity"}
              </button>
            </div>
          )}
      </section>
    </>
  );
}


function ProgrammeActivityRow({
  event,
}) {
  const meta =
    activityMeta(
      event.activity_kind
    );

  const Icon =
    meta.icon;

  const {
    date,
    time,
  } =
    formatDateTime(
      event.event_at
    );

  const hasSingleCohort =
    Boolean(
      event.cohort_id &&
        event.cohort_learner_id
    );

  return (
    <article className="p-5 transition hover:bg-[#FFFDFC] md:p-6">
      <div className="flex gap-4">
        {/* ICON */}

        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>


        {/* CONTENT */}

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              {/* LEARNER */}

              {hasSingleCohort ? (
                <Link
                  to={`/partner/learners/${event.cohort_learner_id}`}
                  className="text-base font-black text-[#333333] transition hover:text-[#E9782F]"
                >
                  {event.learner_full_name ||
                    event.learner_email ||
                    "Learner"}
                </Link>
              ) : (
                <p className="text-base font-black text-[#333333]">
                  {event.learner_full_name ||
                    event.learner_email ||
                    "Learner"}
                </p>
              )}

              {event.learner_email && (
                <p className="mt-0.5 truncate text-xs text-[#999999]">
                  {
                    event.learner_email
                  }
                </p>
              )}


              {/* ACTION */}

              <p className="mt-3 text-sm font-semibold leading-relaxed text-[#555555] md:text-base">
                {event.action_label ||
                  "Programme activity recorded"}
              </p>


              {/* COHORT */}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Building2 className="h-3.5 w-3.5 text-[#999999]" />

                {event.cohort_id ? (
                  <Link
                    to={`/partner/cohorts/${event.cohort_id}`}
                    className="text-xs font-black text-[#757575] transition hover:text-[#E9782F]"
                  >
                    {event.cohort_name ||
                      "Cohort"}
                  </Link>
                ) : (
                  <span className="text-xs font-black text-[#757575]">
                    {event.cohort_name ||
                      "Cohort unavailable"}
                  </span>
                )}
              </div>
            </div>


            {/* META */}

            <div className="flex flex-none flex-row flex-wrap items-center gap-2 lg:flex-col lg:items-end">
              <span
                className={[
                  "inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.08em]",
                  meta.classes,
                ].join(" ")}
              >
                {meta.label}
              </span>

              <div className="flex items-center gap-1.5 text-xs font-bold text-[#757575]">
                <Clock3 className="h-3.5 w-3.5" />

                <span>
                  {date}

                  {time
                    ? ` · ${time}`
                    : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}


function LoadingState() {
  return (
    <div className="rounded-[26px] border border-[#EFEFEF] bg-white p-10 text-center shadow-sm">
      <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />

      <p className="mt-3 text-sm font-bold text-[#757575]">
        Loading programme
        activity…
      </p>
    </div>
  );
}


function EmptyState() {
  return (
    <div className="rounded-[26px] border border-dashed border-[#FFB98B] bg-white p-10 text-center shadow-sm">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
        <CheckCircle2 className="h-6 w-6" />
      </div>

      <h3 className="mt-4 text-lg font-black text-[#333333]">
        No programme
        activity yet
      </h3>

      <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-[#757575]">
        Learner enrolments,
        onboarding milestones,
        learning completions and
        practical completions will
        appear here as they are
        recorded.
      </p>
    </div>
  );
}